import { isNil, countBy, minBy } from 'lodash-es';
import type { Airflow, Dict } from '@aviata/platform-types';
import { citySearchHistory, dictEntity } from 'shared';

import type { SearchPageOpenParams } from '../analytics';
import type { PrepareSearchAnalyticsParams } from './types';
import { CABIN_CLASS_MAP } from './constants';

const extractCityCode = (iata: string) => iata.slice(0, 3);

/**
 * Two one-way legs that form a classic round-trip (A→B then B→A).
 * Two legs where A→B and B→C (multi-city) are NOT a round-trip pair.
 */
const isRoundTripPair = (
  first: { dep?: string; arr?: string },
  second: { dep?: string; arr?: string },
) =>
  first.dep &&
  first.arr &&
  second.dep &&
  second.arr &&
  extractCityCode(first.dep) === extractCityCode(second.arr) &&
  extractCityCode(first.arr) === extractCityCode(second.dep);

const isMultiTripItineraries = (
  itineraries: Array<{ dep?: string; arr?: string }>,
): boolean => {
  if (itineraries.length < 2) {
    return false;
  }
  if (itineraries.length > 2) {
    return true;
  }
  const [first, second] = itineraries;
  if (!first.dep || !first.arr || !second.dep || !second.arr) {
    return false;
  }
  return !isRoundTripPair(first, second);
};

export const getConnectType = (
  connectType: string | null | undefined,
): string => {
  if (!connectType) return 'false';
  return connectType.toLowerCase();
};

export type PartnerFilter =
  | 'ENABLED'
  | 'DISABLED'
  | 'NOT_PARTICIPANT'
  | null
  | undefined;

enum OfferTypes {
  BUSINESS = 'business',
  BUSINESS_IN_ECONOMY = 'business_in_economy',
  ECONOMY = 'economy',
}

export enum SearchResultStatus {
  SUCCESS = 'success',
  ERROR = 'error',
}

export function convertPartnerFilter(partnerFilter: PartnerFilter) {
  const partnerFilterMap = {
    ENABLED: true,
    DISABLED: false,
    NOT_PARTICIPANT: null,
  };

  if (isNil(partnerFilter) || isNil(partnerFilterMap[partnerFilter])) {
    return null;
  }

  return partnerFilterMap[partnerFilter];
}

/**
 * @note made by DanikGPT
 * @example '5-10' in case if request took from 5 to 10 seconds
 */
export function calculateInterval(requestTimeSec: number) {
  const lowerBoundary = Math.floor(requestTimeSec / 5) * 5;
  const upperBoundary = lowerBoundary + 5;

  return `${lowerBoundary}-${upperBoundary}`;
}

export const addOffersCountToEvent = (
  eventProps: Record<string, unknown>,
  offers: Airflow.Offer.Entity[],
) => {
  const metrics = countBy(offers, offer => {
    switch (true) {
      case offer.$meta.cheaper_than_economy:
        return OfferTypes.BUSINESS_IN_ECONOMY;
      case offer.$meta.includes_business:
        return OfferTypes.BUSINESS;
      default:
        return OfferTypes.ECONOMY;
    }
  }) as Record<string, number>;

  eventProps.offers_count = offers.length;
  eventProps.economy_count = metrics.economy ?? 0;
  eventProps.business_in_economy_count = metrics.business_in_economy ?? 0;
  eventProps.business_count = metrics.business ?? 0;
};

export function getSearchTime(requestTimeMs: number): string {
  /**
   * @see https://tracker.yandex.ru/AVIATA-13171
   */
  const MIN_SEC_THRESHOLD_TO_USE_INTERVALS = 10;
  const seconds = Math.round(requestTimeMs / 1000);

  return seconds > MIN_SEC_THRESHOLD_TO_USE_INTERVALS
    ? calculateInterval(seconds)
    : seconds.toString();
}

export const getConnectTypeRange = (offers: Airflow.Offer.Entity[]): string => {
  const connectTypes = offers.map(o => getConnectType(o.$meta.connect_type));
  const hasOwc = connectTypes.some(t => t === 'owc');
  const hasVi = connectTypes.some(t => t === 'vi');
  const allOwc = connectTypes.every(t => t === 'owc');
  const allVi = connectTypes.every(t => t === 'vi');

  if (allOwc) return 'all owc';
  if (allVi) return 'all vi';
  if (hasOwc && hasVi) return 'partly owc and vi';
  if (hasOwc) return 'partly owc';
  if (hasVi) return 'partly vi';
  return 'false';
};

export const extractSearchPageOpenData = (params: SearchPageOpenParams) => {
  const eventProps: Record<string, unknown> = {
    status: params.success
      ? SearchResultStatus.SUCCESS
      : SearchResultStatus.ERROR,
    /**
     * @description
     * used to determine how overall search request timing affects customers
     */
    search_time: getSearchTime(params.searchTimeMs),
    segment_filter_on: isNil(params.segmentFilter)
      ? null
      : Boolean(params.segmentFilter),
    partner_filter: convertPartnerFilter(params.partnerFilter),
    calendar_filter_on: isNil(params.calendarFilter)
      ? null
      : Boolean(params.calendarFilter),
    source: params.source,
  };

  if (params.success && params.offers) {
    addOffersCountToEvent(eventProps, params.offers);

    const cheapestOffer = minBy(params.offers, o => Number(o.price?.amount));

    if (cheapestOffer) {
      eventProps.serp_min_price = Number(cheapestOffer.price.amount);
      eventProps.cheapest_airline_code =
        cheapestOffer.flights[0]?.segments[0]?.display_airline;
    }

    eventProps.travel_type = cheapestOffer?.$meta.is_domestic ? 'D' : 'I';
  }

  if (params.searchParams?.itineraries) {
    const itineraryCount = params.searchParams.itineraries.length;
    if (isMultiTripItineraries(params.searchParams.itineraries)) {
      eventProps.search_mode = 'MT';
    } else if (itineraryCount > 1) {
      eventProps.search_mode = 'RT';
    } else {
      eventProps.search_mode = 'OW';
    }
    eventProps.legs_count = itineraryCount;
  }

  return eventProps;
};

/**
 * Prepares analytics data for search page events
 * @description Consolidates search analytics data preparation into a single function
 */
export const prepareSearchAnalyticsData = (
  params: PrepareSearchAnalyticsParams,
): SearchPageOpenParams => {
  const searchHistory =
    citySearchHistory.model.useCitySearchHistory('free-avia');
  const locations = searchHistory.getCities();

  const firstItinerary = params.searchParams.itineraries[0];
  const { passengers } = params.searchParams;

  const dataForAnalytics: SearchPageOpenParams = {
    success: params.success,
    searchTimeMs: Date.now() - params.searchStartTime,
    hasFilter: params.hasFilter,
    filterParams: params.filterParams,
    partnerFilter: params.partnerFilter,
    segmentFilter: params.segmentFilter,
    calendarFilter: params.calendarFilter,
    source: params.source,
  };

  if (params.success && params.offers) {
    const { itineraries } = params.searchParams;
    const lastItinerary = itineraries[itineraries.length - 1];
    const isMultiTrip = isMultiTripItineraries(itineraries);

    const arrLocation = locations[0];
    const depLocation = locations[locations.length - 1];

    const depCode =
      params.searchParams.dep || itineraries[0]?.dep || depLocation?.code;
    const arrCode = isMultiTrip
      ? lastItinerary?.arr || params.searchParams.arr || arrLocation?.code
      : params.searchParams.arr || arrLocation?.code;

    let cityFromName: string | undefined;
    let cityToName: string | undefined;

    if (params.dict && depCode && arrCode) {
      const dict = params.dict as Dict.Entity;
      cityFromName = dictEntity.lib.getCity(depCode, dict)?.name;
      cityToName = dictEntity.lib.getCity(arrCode, dict)?.name;
    }

    if (!cityFromName) {
      cityFromName = depLocation?.city?.name || depLocation?.name;
    }
    if (!cityToName) {
      cityToName = arrLocation?.city?.name || arrLocation?.name;
    }

    dataForAnalytics.offers = params.offers;
    dataForAnalytics.searchQuery = {
      cityTo: cityToName,
      cityFrom: cityFromName,
      airportTo: arrCode,
      airportFrom: depCode,
      dateFrom: firstItinerary.date,
      dateTo: isMultiTrip
        ? lastItinerary?.date || null
        : itineraries[1]?.date || null,
      cabinClass: params.searchParams.cabinClass,
      passengerAdult: passengers.adt || 0,
      passengerYouth: passengers.yth || 0,
      passengerChild: passengers.chd || 0,
      passengerTotal:
        (passengers.adt || 0) + (passengers.yth || 0) + (passengers.chd || 0),
    };
    // Pass dict and searchParams for Bloomreach to extract city data
    dataForAnalytics.dict = params.dict;
    dataForAnalytics.searchParams = params.searchParams;
  } else if (!params.success && params.error) {
    dataForAnalytics.error = params.error;
  }

  return dataForAnalytics;
};

/**
 * Helper to get city info from dictionary with all localized names
 * Shared function used by bloomreach-events and accelera-events
 */
export const getCityInfoFromDict = (
  cityCode: string,
  dict: Dict.Entity,
  direction: 'to' | 'from',
) => {
  const city = dictEntity.lib.getCity(cityCode, dict);
  if (!city) {
    return {};
  }
  return {
    [`city_${direction}_iata`]: city.iata,
    [`city_${direction}_name`]: city.name,
    [`city_${direction}_name_ru`]: city.name_ru,
    [`city_${direction}_name_kk`]: city.name_kk,
    [`city_${direction}_name_en`]: city.name_en,
    [`city_${direction}_country_iso_code`]: city.country?.iso_code,
    [`city_${direction}_country_name`]: city.country?.name,
    [`city_${direction}_country_name_ru`]: city.country?.name_ru,
    [`city_${direction}_country_name_kk`]: city.country?.name_kk,
    [`city_${direction}_country_name_en`]: city.country?.name_en,
  };
};

/**
 * Enriches event data with city information from dictionary
 * Shared function used by bloomreach-events and accelera-events
 */
export const enrichEventWithCityData = (
  eventData: Record<string, unknown>,
  params: SearchPageOpenParams,
) => {
  if (!params.dict || !params.searchParams?.dep || !params.searchParams?.arr) {
    return;
  }

  const dict = params.dict as Dict.Entity;
  const { itineraries } = params.searchParams;
  const lastItineraryArr =
    itineraries && isMultiTripItineraries(itineraries)
      ? itineraries[itineraries.length - 1]?.arr
      : undefined;

  const cityFromData = getCityInfoFromDict(
    params.searchParams.dep,
    dict,
    'from',
  );
  const cityToData = getCityInfoFromDict(
    lastItineraryArr ?? params.searchParams.arr,
    dict,
    'to',
  );
  Object.assign(eventData, cityFromData, cityToData);
};

/**
 * Enriches event data with passenger information
 * Shared function used by bloomreach-events and accelera-events
 */
export const enrichEventWithPassengerData = (
  eventData: Record<string, unknown>,
  params: SearchPageOpenParams,
) => {
  if (params.searchParams?.passengers) {
    eventData.passenger_adult = params.searchParams.passengers.adt || 0;
    eventData.passenger_youth = params.searchParams.passengers.yth || 0;
    eventData.passenger_child = params.searchParams.passengers.chd || 0;
    eventData.passenger_infant = params.searchParams.passengers.inf || 0;
  }
};

/**
 * Enriches event data with cabin class
 * Shared function used by bloomreach-events and accelera-events
 */
export const enrichEventWithCabinClass = (
  eventData: Record<string, unknown>,
  params: SearchPageOpenParams,
) => {
  if (params.searchParams?.cabinClass) {
    eventData.class_type =
      CABIN_CLASS_MAP[params.searchParams.cabinClass] ||
      params.searchParams.cabinClass.toLowerCase();
  }
};

/**
 * Enriches event data with dates and search mode
 * Shared function used by bloomreach-events and accelera-events
 */
export const enrichEventWithDatesAndSearchMode = (
  eventData: Record<string, unknown>,
  params: SearchPageOpenParams,
) => {
  const { itineraries } = params.searchParams ?? {};
  if (itineraries && itineraries[0]) {
    eventData.date_from = itineraries[0].date;

    if (isMultiTripItineraries(itineraries)) {
      eventData.date_to = itineraries[itineraries.length - 1].date;
      eventData.search_mode = 'mt';
    } else if (itineraries[1]) {
      eventData.date_to = itineraries[1].date;
      eventData.search_mode = 'rt';
    } else {
      eventData.search_mode = 'ow';
    }
  }
};

/**
 * Enriches event data with travel type (international/domestic)
 * Shared function used by bloomreach-events and accelera-events
 */
export const enrichEventWithTravelType = (
  eventData: Record<string, unknown>,
  params: SearchPageOpenParams,
) => {
  if (!params.dict || !params.searchParams) {
    return;
  }

  const dict = params.dict as Dict.Entity;
  const { itineraries } = params.searchParams;

  const allCityCodes = new Set<string>();
  if (itineraries) {
    for (const it of itineraries) {
      if (it.dep) {
        allCityCodes.add(extractCityCode(it.dep));
      }
      if (it.arr) {
        allCityCodes.add(extractCityCode(it.arr));
      }
    }
  } else {
    if (params.searchParams.dep) {
      allCityCodes.add(params.searchParams.dep);
    }
    if (params.searchParams.arr) {
      allCityCodes.add(params.searchParams.arr);
    }
  }

  const countries = new Set<string>();
  for (const code of allCityCodes) {
    const city = dictEntity.lib.getCity(code, dict);
    if (city?.country?.iso_code) {
      countries.add(city.country.iso_code);
    }
  }

  if (countries.size > 0) {
    eventData.travel_type = countries.size > 1 ? 'international' : 'domestic';
  }
};
